"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { cn } from "../shared/utils/utils"
import { Button } from "../dashboard/company-dashboard/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dashboard/company-dashboard/ui/dropdown-menu"
import Requests from "./requests/requests"
import CreateTemplate from "./create-questionare-template/CreateTemplate"
import RequestRating from "./create-questionare-template/RequestRating"
import RateeView from "./create-questionare-template/RateeView"
import Resubmissions from "./requests/Resubmissions"
import ValuesView from "./questionnaire/views/ValuesView"
import LeadershipView from "./questionnaire/views/LeadershipView"
import Templates from "./questionnaire/components/CustomisedTemplates"
import LowRated from "./Reports360/LowRated"
import AllReports from "./Reports360/AllReports"
import Department from "./Reports360/Department"
import Dimension from "./Reports360/Dimension"
import TopRated from "./Reports360/TopRated"
import AdminDashboard from "./dashboard/AdminDashboard"
import { ChevronDown, BarChart3, Users, FileText, PieChart, Flag } from "lucide-react"
import FlaggedRequests from "./flagged-requests/FlaggedRequests"

interface TabButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
  icon?: React.ReactNode
  dropdownItems?: { label: string; onClick: () => void }[]
}

const TabButton: React.FC<TabButtonProps> = ({ label, isActive, onClick, icon, dropdownItems }) => {
  if (dropdownItems && dropdownItems.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-200 border-2 border-transparent hover:scale-105",
              isActive
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-200",
            )}
          >
            {icon && <span className="text-sm sm:text-lg">{icon}</span>}
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{label.split(" ")[0]}</span>
            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-48 sm:w-56 p-2 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-xl rounded-xl"
        >
          {dropdownItems.map((item, index) => (
            <DropdownMenuItem
              key={index}
              onClick={item.onClick}
              className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors text-sm"
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-200 border-2 border-transparent hover:scale-105",
        isActive
          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-200",
      )}
    >
      {icon && <span className="text-sm sm:text-lg">{icon}</span>}
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label.split(" ")[0]}</span>
    </Button>
  )
}

const MainAdmin = observer(() => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const tabFromUrl = queryParams.get("tab")
  const [activeTab, setActiveTab] = useState<string>(() => {
    return tabFromUrl || localStorage.getItem("activeTab") || "dashboard"
  })

  const handleTab = (label: string) => {
    setActiveTab(label)
    localStorage.setItem("activeTab", label)
    const newQueryParams = new URLSearchParams(location.search)
    newQueryParams.set("tab", label)
    navigate(`${location.pathname}?${newQueryParams.toString()}`)
  }

  useEffect(() => {
    const tab = queryParams.get("tab")
    if (tab) {
      setActiveTab(tab)
    }
  }, [location.search])

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab)
  }, [activeTab])

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header - Full Width */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1 sm:mb-2">
          360° Admin Dashboard
        </h1>
        <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
          Comprehensive administration and management platform
        </p>
      </div>

      {/* Main Content Card - Full Width */}
      <div className="w-full bg-white/80 backdrop-blur-sm shadow-xl border-t border-white/20">
        {/* Navigation Tabs - Full Width */}
        <div className="w-full bg-white/50 border-b border-slate-200/50 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <TabButton
              label="Dashboard"
              isActive={activeTab === "dashboard"}
              onClick={() => handleTab("dashboard")}
              icon={<BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />}
            />
            <TabButton
              label="Surveys"
              isActive={activeTab.startsWith("surveys")}
              onClick={() => {}}
              icon={<Users className="h-3 w-3 sm:h-4 sm:w-4" />}
              dropdownItems={[
                { label: "💎 Values", onClick: () => handleTab("surveys-values") },
                { label: "👑 Leadership", onClick: () => handleTab("surveys-leadership") },
                { label: "📋 Templates", onClick: () => handleTab("surveys-templates") },
              ]}
            />
            <TabButton
              label="Requests"
              isActive={activeTab === "requests"}
              onClick={() => handleTab("requests")}
              icon={<FileText className="h-3 w-3 sm:h-4 sm:w-4" />}
            />
            <TabButton
              label="Reports"
              isActive={activeTab.startsWith("reports")}
              onClick={() => {}}
              icon={<PieChart className="h-3 w-3 sm:h-4 sm:w-4" />}
              dropdownItems={[
                { label: "📉 Low Rated", onClick: () => handleTab("reports-low-rated") },
                { label: "📈 Top Rated", onClick: () => handleTab("reports-top-rated") },
                { label: "🏢 Department", onClick: () => handleTab("reports-department") },
                { label: "📊 Dimension", onClick: () => handleTab("reports-dimension") },
                { label: "📋 All Reports", onClick: () => handleTab("reports-all-reports") },
              ]}
            />
            <TabButton
              label="Flagged Requests"
              isActive={activeTab === "flagged-requests"}
              onClick={() => handleTab("flagged-requests")}
              icon={<Flag className="h-3 w-3 sm:h-4 sm:w-4" />}
            />
          </div>
        </div>

        {/* Content Area - Full Width */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {activeTab === "dashboard" && <AdminDashboard />}
          {activeTab === "surveys-values" && <ValuesView />}
          {activeTab === "surveys-leadership" && <LeadershipView />}
          {activeTab === "surveys-templates" && <Templates />}
          {activeTab === "requests" && <Requests />}
          {activeTab === "reports-low-rated" && <LowRated />}
          {activeTab === "reports-top-rated" && <TopRated />}
          {activeTab === "reports-department" && <Department />}
          {activeTab === "reports-dimension" && <Dimension />}
          {activeTab === "reports-all-reports" && <AllReports />}
          {activeTab === "template" && <CreateTemplate />}
          {activeTab === "RequestRating" && <RequestRating />}
          {activeTab === "RateeView" && <RateeView />}
          {activeTab === "flagged-requests" && <FlaggedRequests />}
        </div>
      </div>
    </div>
  )
})

export default MainAdmin
